/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Message Thunk
 */

import { getAPIFriendlyFilters } from '@zcode-ui/filters'
import {
  getUser,
  onMessageSent,
  selectIsMobile,
  selectIsOnline,
} from '@zcode-ui/web-zumper-global-state'
import { UserMessageTraits } from '@zcode/enums'
import { USER_ID_LOCAL_STORAGE_KEY } from '@zcode/integrations'
import { getGclidFromLocalStorage, getMsclkidFromLocalStorage } from '@zcode/legacy-local-storage'
import { TOUR_PREFERENCE_TYPES, identify } from '@zcode/mixpanel'
import { AnalyticsC, Origin, isNotMultiMessageOrigin, localStorageSingleton } from '@zcode/nr-utils'
import { handleButter } from '@zcode/nr/es/shared/actions/sendMessage/helper/handleButter'
import handleErrorAnalyticsAndButter from '@zcode/nr/es/shared/actions/sendMessage/helper/handleErrorAnalyticsAndButter'
import saveMessageUser from '@zcode/nr/es/shared/actions/sendMessage/helper/saveMessageUser'
import setupMessageQuery from '@zcode/nr/es/shared/actions/sendMessage/helper/setupMessageQuery'
import trackLeadConversion from '@zcode/nr/es/shared/actions/sendMessage/helper/trackLeadConversion'
import trackOptimizelyMessageEvents from '@zcode/nr/es/shared/actions/sendMessage/helper/trackOptimizelyMessageEvents'
import * as Api from '@zcode/nr/es/shared/api'
import { APPLY, TOUR } from '@zcode/nr/es/shared/constants/message'
import { warnOffline } from '@zcode/nr/es/shared/modules/app/thunks'
import { selectIsLoggedIn } from '@zcode/nr/es/shared/modules/user/selectors'
import { requeue } from '@zcode/nr/es/shared/packages/offline-middleware/actions'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import { MESSAGE_SENT_MAPLIST } from '@zcode/nr/es/shared/thirdParty/optimizely/constants'
import Conversion from '@zcode/nr/es/shared/utils/Conversion'
import { trackMessageModification } from '@zcode/nr/es/shared/utils/analytics'
import { getVisitedExperiments } from '@zcode/optimizely-client'
import type { ExtraThunkArg, Profile, SendMessageArgs } from '@zcode/state-type'
import { ONE_MINUTE } from '@zcode/utils'
import type { Dispatch } from 'redux'
import { getViewCountObject } from '../../components/detail/helpers/viewCount/viewCountObject'
import { getHasInstantTourAvailability } from '../../components/message/MessageForm/TourOptions/getHasInstantTourAvailability'
import { getFavorited, getMessagedSize } from '../../inspectors/index'
import { logger } from '../../logger'
import { setLastClickedEntity } from '../../modules/currentSearch/actions/index'
import { selectWasCurrentEntityOnceAFilterAd } from '../../modules/currentSearch/selectors/index'
import { selectFilters } from '../../modules/filters/selectors/index'
import { getFormattedOrigin } from '../../modules/mixpanel/helpers/getFormattedOrigin'
import { getPageNameFromRouteProps } from '../../modules/mixpanel/helpers/getPageNameFromRouteProps'
import { mixpanelSendMessage } from '../../modules/mixpanel/thunks/mixpanelSendMessage'
import { mixpanelSendWaitlistMessage } from '../../modules/mixpanel/thunks/mixpanelSendWaitlistMessage'
import { setProfile } from '../../modules/profile/actions/index'
import { getProfileShareId } from '../../modules/profileShare/thunks/getProfileShareId'
import { loadConversationPostLead } from '../../modules/stream/thunks/loadConversationPostLead'
import type { GetState } from '../../modules/types'
import {
  DLC_SAVED_SEARCH_OPT_IN,
  HIGH_INTENT_LEAD_SENT,
  HIGH_INTENT_LEAD_SENT_FLOORPLAN,
  LEAD_SENT_FLOORPLAN,
  MESSAGE_SENT_DETAIL_AGENT,
  MESSAGE_SENT_NON_CAROUSEL,
  SHORT_TERM_LEAD_SENT,
  TOUR_BOOKING_LEAD_SENT,
  TOUR_INTENT_LEAD_SENT,
} from '../../packages/optimizely/constants/events'
import { getMessageFavoritesPayload } from '../helpers/getMessageFavoritesPayload'
import { handleFollowUpModalAndSuccessAnalyticsAndButter } from './handleFollowUpModalAndSuccessAnalyticsAndButter'
import { setRequestedTour } from './requestedTours'
import { setupMessageData } from './setupMessageData'

function assertIsSendMessageSuccessResponse(
  result: Api.SendMessageResponse
): asserts result is Api.SendMessageSuccessResponse {
  if (result.status < 200 || result.status > 202) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw result
  }
}

/**
 * send message thunk:
 * prepare the message arguments to the api, and on completion track analytics, and dispatch
 * actions according to login state
 *
 * @param sendMessageArgs
 */
export function sendMessage(
  sendMessageArgs: undefined
): (dispatch: Dispatch, getState: GetState, extra: ExtraThunkArg) => Promise<undefined>
export function sendMessage(
  sendMessageArgs: SendMessageArgs
): (
  dispatch: Dispatch,
  getState: GetState,
  extra: ExtraThunkArg
) => Promise<Api.SendMessageSuccessResponse>
export function sendMessage(
  sendMessageArgs: SendMessageArgs | undefined
): (
  dispatch: Dispatch,
  getState: GetState,
  extra: ExtraThunkArg
) => Promise<Api.SendMessageSuccessResponse | undefined>
export function sendMessage(
  sendMessageArgs: SendMessageArgs | undefined
): (
  dispatch: Dispatch,
  getState: GetState,
  extra: ExtraThunkArg
) => Promise<Api.SendMessageSuccessResponse | undefined> {
  if (!sendMessageArgs) {
    return async () => undefined
  }
  return async (
    dispatch: Dispatch,
    getState: GetState,
    { optimizely }: ExtraThunkArg
  ): Promise<Api.SendMessageSuccessResponse> => {
    const {
      analyticChannel,
      dlcEnabled,
      dlcMessagedProperties,
      entity,
      equalCTAIntent,
      floorplan,
      formType,
      isMulti,
      isResidentQuotesChat,
      messageData: messageDataConfig,
      moveInText,
      multiListables,
      oneTap,
      selectedFloorplanId,
      shouldLimitLeads,
      showMessageSuccessButter,
      showTourSuccessButterBar,
    } = sendMessageArgs
    // it's silly, but it makes TypeScript happy
    const isMultiAndMultiListables = isMulti
      ? { isMulti, multiListables }
      : { dlcMessagedProperties, isMulti, multiListables }
    const state = getState()

    const filters = selectFilters(state)
    const user = getUser(state)
    const isMobile = selectIsMobile(state)
    const isOnline = selectIsOnline(state)
    const isLoggedIn = selectIsLoggedIn(state)
    const listablesQuery = getAPIFriendlyFilters({ filters: filters || {} })
    const favorites = getMessageFavoritesPayload(getFavorited(state))
    const profileShareId = await dispatch(getProfileShareId)
    const isFirstMessage = getMessagedSize(state) === 0
    const isFilterAd = selectWasCurrentEntityOnceAFilterAd(state, entity)
    if (dlcEnabled) {
      void dispatch(Optimizely.track({ eventKey: DLC_SAVED_SEARCH_OPT_IN }))
    }
    // TODO: Find a way to make this offline flow work for buildings. Listings are fine to message offline,
    // but buildings need listing_ids to successfully send a message. Listing_ids are not preloaded into
    // the message modal, so should we default to the first listing_id in the array of floor plans if offline??
    // There is a ticket for this here: https://github.com/zumper/zumper_code/issues/23566
    if (!isOnline) {
      let warningMessage =
        'It seems that you have been disconnected. Please try again once you reconnect.'
      if (entity?.isListing) {
        // message changes because we are currently only able to requeue listings offline
        warningMessage =
          'It seems that you have been disconnected. We will try to send the message once you reconnect.'
        const callback = async () =>
          await dispatch(
            sendMessage({
              analyticChannel,
              entity,
              formType,
              messageData: messageDataConfig,
              selectedFloorplanId,
              oneTap,
              ...isMultiAndMultiListables,
            })
          )
        const options = { before: ONE_MINUTE }
        const name = 'sendMessage'
        dispatch(requeue({ callback, options, name }))
      }
      // alert message here to remind users that they are offline and to stop spamming
      // the message api
      dispatch(warnOffline({ message: warningMessage, repeat: true }))
      return await Promise.reject(new Error('connection error: sendMessage'))
    }

    const timestamp = ~~(Date.now() / 1000)

    const messageData = setupMessageData({
      equalCTAIntent,
      entity,
      formType,
      isMobile,
      messageData: messageDataConfig,
      selectedFloorplanId,
      ...isMultiAndMultiListables,
    })
    const { messageModified, messagedBuildingIds, messagedListingIds, msg, selectedTimes } =
      messageData

    // Optimizely visited experiments
    const visitedExperiments = getVisitedExperiments(optimizely)

    let query: ReturnType<typeof setupMessageQuery>
    try {
      query = setupMessageQuery({
        config: {
          entity,
          isMobile,
          isMulti,
        },
        messageData,
        oneTap,
        variationMap: visitedExperiments,
      })
    } catch (e) {
      logger.error('messaging without property id', isMulti, entity)
      dispatch(
        handleButter({
          msg: 'Sorry, unable to message some properties at this time.',
          duration: 5000,
        })
      )
      return await Promise.reject(e)
    }

    const { message } = query

    // track message modification
    if (messageModified) {
      trackMessageModification(msg, 'm')
    }

    let hasInstantTourAvailability: boolean | undefined

    // track mixpanel event
    if (window?.location?.pathname) {
      let { origin } = messageDataConfig || {}
      if (isMulti && origin && isNotMultiMessageOrigin(origin)) {
        origin = `${origin}-mm`
      }
      const formattedOrigin = getFormattedOrigin(origin)
      const pageName = getPageNameFromRouteProps({
        routeProps: {
          location: { pathname: window.location.pathname, search: window.location.search },
        },
        isMobile,
      })
      if (equalCTAIntent === 'Waitlist') {
        dispatch(mixpanelSendWaitlistMessage({ entity, pageName, selectedFloorplanId }))
      } else {
        dispatch(
          mixpanelSendMessage({
            pageName,
            entity,
            origin,
            formattedOrigin,
            tourPreferenceCount: selectedTimes?.length ?? 0,
            tourPreferenceType: selectedTimes?.length
              ? entity.isTour
                ? // biome-ignore lint/suspicious/noAssignInExpressions:
                  (hasInstantTourAvailability ??= await dispatch(
                    getHasInstantTourAvailability(entity)
                  ))
                  ? TOUR_PREFERENCE_TYPES.KNOCK
                  : TOUR_PREFERENCE_TYPES.OPEN_HOUSE
                : TOUR_PREFERENCE_TYPES.AVAILABILITY
              : undefined,
          })
        )
      }
    }

    // mixpanel identify user email
    const email = messageDataConfig?.email
    email && identify(email)
    // send message and attach resolve and reject behaviors that should occur on every send.
    let response: Api.SendMessageSuccessResponse | null = null
    const messagingApi = isResidentQuotesChat ? Api.residentQuotesChat : Api.message
    if (isResidentQuotesChat && !message.building_ids?.length && entity.isBuilding) {
      message.building_ids = [entity.buildingId]
    }
    return await messagingApi
      .send({
        ...message,
        filters: listablesQuery,
        detail_views: getViewCountObject(),
        favorites,
        gclid: getGclidFromLocalStorage(localStorageSingleton),
        msclkid: getMsclkidFromLocalStorage(localStorageSingleton),
        profile_share_id: profileShareId,
        traktor_id: localStorageSingleton.get(USER_ID_LOCAL_STORAGE_KEY),
        ...(isFilterAd
          ? { traits: [UserMessageTraits.FILTER_AD] }
          : isResidentQuotesChat && { traits: [UserMessageTraits.RESIDENT_QUOTE] }),
      })
      .then((result: Api.SendMessageResponse) => {
        // should always be 202. 202 is "message submitted successfully"
        // 200 is "message still being processed", 201 is "message send completed"
        assertIsSendMessageSuccessResponse(result)
        return result
      })
      .then(
        async (result) => {
          // message send was a success
          response = result
          if (!isMulti && entity) {
            const { isMonetizedM3 } = entity
            if (isMonetizedM3) {
              Conversion.trackLead()
            }
          } else if (isMulti && multiListables) {
            for (const listable of multiListables) {
              const { isMonetizedM3 } = listable
              if (isMonetizedM3) {
                Conversion.trackLead()
              }
            }
          }
          const { origin } = messageDataConfig
          const isHighIntentLead = equalCTAIntent === APPLY || equalCTAIntent === TOUR
          if (equalCTAIntent === TOUR) {
            if (
              // biome-ignore lint/suspicious/noAssignInExpressions:
              (hasInstantTourAvailability ??= await dispatch(getHasInstantTourAvailability(entity)))
            ) {
              dispatch(Optimizely.track({ eventKey: TOUR_BOOKING_LEAD_SENT }))
            } else {
              dispatch(Optimizely.track({ eventKey: TOUR_INTENT_LEAD_SENT }))
            }
          }
          if (entity?.isShortTerm) {
            dispatch(Optimizely.track({ eventKey: SHORT_TERM_LEAD_SENT }))
          }
          if (isMobile && !isMulti) {
            dispatch(Optimizely.track({ eventKey: MESSAGE_SENT_NON_CAROUSEL }))
          }
          if (isHighIntentLead) {
            dispatch(Optimizely.track({ eventKey: HIGH_INTENT_LEAD_SENT }))
          }
          if (
            origin === Origin.DETAIL_AGENT_BOX ||
            origin === Origin.MOBILE_DETAIL_AGENT_BOX ||
            origin === Origin.ADDR_DETAIL_AGENT_BOX
          ) {
            dispatch(Optimizely.track({ eventKey: MESSAGE_SENT_DETAIL_AGENT }))
          }
          if (origin?.includes(Origin.FLOORPLAN)) {
            dispatch(Optimizely.track({ eventKey: LEAD_SENT_FLOORPLAN }))
            isHighIntentLead &&
              dispatch(Optimizely.track({ eventKey: HIGH_INTENT_LEAD_SENT_FLOORPLAN }))
          }
          if (analyticChannel === AnalyticsC.LIST && !isMulti) {
            dispatch(Optimizely.track({ eventKey: MESSAGE_SENT_MAPLIST }))
          }
          if (isFilterAd) {
            dispatch(setLastClickedEntity({}))
          }
          void dispatch(
            loadConversationPostLead({
              buildingId: entity.buildingId,
              listingId: entity.listingId ?? selectedFloorplanId,
              messageId: result.message_id,
            })
          )
          trackLeadConversion({
            entity,
            floorplan,
            ...isMultiAndMultiListables,
          })
          // dispatch that message was sent successfully
          dispatch(
            onMessageSent({
              success: true,
              buildingIds: messagedBuildingIds,
              equalCTAIntent,
              listingIds: messagedListingIds,
              timestamp,
            })
          )

          dispatch(
            saveMessageUser({
              config: {
                entity,
                isMulti,
                isLoggedIn,
              },
              equalCTAIntent,
              messageData,
              moveInText,
              setProfile: (profile: Profile) => dispatch(setProfile(profile)),
              timestamp,
              user,
            })
          )
          dispatch(trackOptimizelyMessageEvents({ entity, ...isMultiAndMultiListables }))

          const showPostLeadAfterMM = isFirstMessage && !isLoggedIn
          const dlcProps = {
            equalCTAIntent,
            formType,
            selectedFloorplanId,
          }
          void dispatch(
            handleFollowUpModalAndSuccessAnalyticsAndButter({
              config: {
                analyticChannel,
                entity,
                ...isMultiAndMultiListables,
              },
              filters,
              messageData,
              shouldLimitLeads,
              showMessageSuccessButter,
              showPostLeadAfterMM,
              ...(showTourSuccessButterBar
                ? {
                    // biome-ignore lint/suspicious/noAssignInExpressions:
                    hasInstantTourAvailability: (hasInstantTourAvailability ??= await dispatch(
                      getHasInstantTourAvailability(entity)
                    )),
                    showTourSuccessButterBar,
                  }
                : { showTourSuccessButterBar }),
              ...(dlcEnabled && dlcProps),
            })
          )
          localStorageSingleton.set('one_tap_form_inputs', {
            ...(localStorageSingleton.get('one_tap_form_inputs') || {}),
            email,
            moveIn: messageDataConfig.moveIn,
            name: messageDataConfig.name,
            phone: messageDataConfig.phone,
            ...(floorplan && { floorplan }),
          })
          if (messageData.selectedTimes) {
            setRequestedTour(
              entity,
              // biome-ignore lint/suspicious/noAssignInExpressions:
              (hasInstantTourAvailability ??= await dispatch(
                getHasInstantTourAvailability(entity)
              )),
              messageData,
              floorplan?.listingId
            )
          }

          return result
        },
        async (err: unknown) => {
          dispatch(
            handleErrorAnalyticsAndButter({
              config: {
                analyticChannel,
                isMulti,
              },
              err,
              message,
              messageData,
            })
          )
          return await Promise.reject(err)
        }
      )
      .catch(async (err: unknown) => {
        // collect errors in either then/err handler above
        if (response === null) {
          if (
            !isLoggedIn &&
            err &&
            typeof err === 'object' &&
            'status' in err &&
            err.status === 450
          ) {
            // account exists for this email already
            void dispatch(
              handleFollowUpModalAndSuccessAnalyticsAndButter({
                // TODO: all these params should be cleaned up.
                config: {
                  analyticChannel,
                  entity,
                  ...isMultiAndMultiListables,
                },
                filters,
                messageData,
                showPreLeadSignInModal: true,
                sendMessageArgs,
              })
            )
          } else if (!err || typeof err !== 'object' || !('status' in err) || !err.status) {
            logger.error('message was not sent', message, err)
          }
          return await Promise.reject(err)
        }
        err &&
          (typeof err !== 'object' || !('status' in err) || err.status !== 450) &&
          logger.error('error after message.send success', response, message, err)
        return response
      })
  }
}
